// MONITORING surface (N-FDE-2) — "what did the agent do, when, on whose behalf, at what cost". The dashboard
// renders the `agent` (B) run/cost traces + the kernel SANITISED audit activity + the gate-refusal/failure
// COUNTS (derived from the same audit projection) + Connector health — each composed by the Stage-1
// `composeMonitoringView`. It is READ-ONLY: the surface routes no write. Tokens only (G-181); every displayed
// state DERIVES from the owning lifecycle (G-182).
//
// SCOPE HONESTLY (N-FDE-2 AC-6, G-180): the caps + cost + activity + counts are an ALWAYS-PERMITTED aggregate
// scoped by the OWNING interfaces for the resolved Actor. A verbatim transcript is a SEPARATE, actor-scoped
// read routed per run (RunTraceList) — the OWNING agent read refuses an Actor without the cross-user oversight
// grant and the surface renders no transcript. The dashboard renders EXACTLY what it gets and widens nothing.
//
// CAPS SHOWN AGAINST COST (N-FDE-2 AC-2): the £50/day spike + £150/week binding caps render ALONGSIDE the
// spend figures with the pre-spend-reservation posture stated honestly (the cap is enforced by kernel/agent,
// not here — the surface surfaces it, G-044).

import { useCallback } from 'react';
import { useSurfaceApi } from './SurfaceApiContext.tsx';
import { useProjection } from './useProjection.ts';
import { SurfaceFrame } from './surface-frame.tsx';
import { SectionHead } from './primitives.tsx';
import { ScreenToolbar, ScreenBody, TbStamp, TbInfo } from '../app/screen-chrome.tsx';
import { RunTraceList } from './components/RunTraceList.tsx';
import { MonitoringActivity } from './components/MonitoringActivity.tsx';
import { MonitoringOutcomeSummary } from './components/MonitoringOutcomeSummary.tsx';
import { CostAgainstCap, CountBars, outcomeTone } from './components/operate-primitives.tsx';
import type { CountBarRow } from './components/operate-primitives.tsx';
import type {
  MonitoringProjection,
  OutcomeCountView,
  RunTranscriptProjection,
} from './operate-projections.ts';

/** A readable label for an audit outcome (display only — the id/enum is the behavioural signal). */
const OUTCOME_LABEL: Readonly<Record<string, string>> = {
  committed: 'Committed',
  recorded: 'Recorded',
  gate_refusal: 'Gate refused',
  egress_denied: 'Egress denied',
  ssrf_blocked: 'Network blocked',
  command_refused: 'Command refused',
  cost_cap_refused: 'Cost cap refused',
  rate_limited: 'Rate limited',
};

/** Turn the outcome tally into count-bar rows (the refusal/failure graph — N-FDE-2 AC-4). */
function toCountBars(counts: readonly OutcomeCountView[]): readonly CountBarRow[] {
  return counts.map((c) => ({
    key: c.outcome,
    label: OUTCOME_LABEL[c.outcome] ?? c.outcome,
    count: c.count,
    tone: outcomeTone(c.outcome),
  }));
}

/** The Monitoring surface. */
export function MonitoringSurface(): React.JSX.Element {
  const api = useSurfaceApi();
  const state = useProjection<MonitoringProjection>(api.getMonitoring);
  const onViewTranscript = useCallback((runId: string) => api.getRunTranscript(runId), [api]);
  return (
    <>
      <ScreenToolbar title="Monitoring">
        {state.data !== null ? <TbStamp>{state.data.runs.length} runs in view</TbStamp> : null}
        <TbInfo
          title="Monitoring"
          tip="What the agent did, when, on whose behalf, at what cost — with spend shown against the caps and every refusal graphed, not grepped."
        />
      </ScreenToolbar>
      <ScreenBody>
        <SurfaceFrame state={state}>
          {(view) => <MonitoringBody view={view} onViewTranscript={onViewTranscript} />}
        </SurfaceFrame>
      </ScreenBody>
    </>
  );
}

/** The loaded Monitoring body — cost-against-caps, refusal/failure counts, run traces, and audit activity. */
function MonitoringBody({
  view,
  onViewTranscript,
}: {
  readonly view: MonitoringProjection;
  readonly onViewTranscript: (runId: string) => Promise<RunTranscriptProjection>;
}): React.JSX.Element {
  return (
    <div className="space-y-8" data-monitoring>
      <section aria-label="Run outcomes">
        <SectionHead label="Outcomes · search by visible title" />
        <MonitoringOutcomeSummary rows={view.runOutcomes} />
      </section>
      <MonitoringDiagnostics view={view} onViewTranscript={onViewTranscript} />
    </div>
  );
}

/** Existing low-level evidence stays read-only and actor-scoped behind an initially collapsed disclosure. */
function MonitoringDiagnostics({
  view,
  onViewTranscript,
}: {
  readonly view: MonitoringProjection;
  readonly onViewTranscript: (runId: string) => Promise<RunTranscriptProjection>;
}): React.JSX.Element {
  return (
    <details data-monitoring-diagnostics>
      <summary className="cursor-pointer text-sm font-medium text-text">
        Detailed diagnostics
      </summary>
      <div className="mt-6 space-y-8">
        <CostDiagnostics view={view} />
        <OutcomeCountDiagnostics counts={view.outcomeCounts} />
        <section aria-label="Run traces">
          <SectionHead label="Agent runs · cost & traces" />
          <RunTraceList runs={view.runs} onViewTranscript={onViewTranscript} />
        </section>
        <section aria-label="Audit activity">
          <SectionHead label="Activity · audit trail" />
          <MonitoringActivity rows={view.activity} />
        </section>
      </div>
    </details>
  );
}

function CostDiagnostics({ view }: { readonly view: MonitoringProjection }): React.JSX.Element {
  return (
    <section aria-label="Cost against caps">
      <SectionHead label="Cost · shown against the caps" />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <CostAgainstCap
          label="This window"
          spentPence={view.spend.dailySpentPence}
          capPence={view.caps.dailySpikePence}
          note="£50/day spike guard — a run is refused before it can spend past the cap."
        />
        <CostAgainstCap
          label="Weekly ceiling"
          spentPence={view.spend.weeklySpentPence}
          capPence={view.caps.weeklyBindingPence}
          note="£150/week binding ceiling — spend is reserved pre-run against it (G-044)."
        />
      </div>
    </section>
  );
}

function OutcomeCountDiagnostics({
  counts,
}: {
  readonly counts: readonly OutcomeCountView[];
}): React.JSX.Element {
  return (
    <section aria-label="Gate-refusal and failure counts">
      <SectionHead label="Gate refusals & failures" />
      {counts.length > 0 ? (
        <CountBars rows={toCountBars(counts)} label="Outcome counts" />
      ) : (
        <p className="text-sm text-text-subtle">No recorded outcomes in this window.</p>
      )}
    </section>
  );
}
